import axios from "axios"

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/ld+json",
  },
})

const endpoints = {
  activities: "store-activities",
  stores: "stores",
  storeUsers: "user-stores",
  activateUser: "users/activate",
  auth: "auth",
  resendVerification: process.env.REACT_APP_PARTNER_RESEND_VERIFICATION_ENDPOINT || "store-users/resend-verification",
}

const normalizeCollection = data =>
  data?.["hydra:member"] || data?.member || (Array.isArray(data) ? data : [])

export const getPartnerRegistrationConflictData = error => {
  if (error?.response?.status !== 409) return null

  const data = error.response.data || {}
  const store = data.store || data.existingStore || data.resource || {}
  const user = data.user || data.existingUser || data.admin || {}

  return {
    registrationToken:
      data.registrationToken || data.registration_token || store.registrationToken || store.registration_token || "",
    storeId: data.storeId || data.store_id || store.id || "",
    email: data.email || data.userEmail || data.user_email || user.email || "",
    userExists: Boolean(
      data.userExists || data.user_exists || data.adminExists || data.admin_exists ||
      data.hasUser || data.has_user || user.email
    ),
  }
}

export const getStoreActivities = async () => {
  const response = await api.get(endpoints.activities)
  return normalizeCollection(response.data)
}

export const createPartnerStore = async values => {
  const response = await api.post(endpoints.stores, {
    name: values.name.trim(),
    address: values.address.trim(),
    zipCode: Number(values.zipCode),
    city: values.city.trim(),
    country: values.country.trim(),
    location: `POINT(${Number(values.location.longitude)} ${Number(values.location.latitude)})`,
    website: values.website?.trim() || null,
    acceptCash: values.acceptCash,
    activity: `/v1/api/store-activities/${values.activityId}`,
  })

  return response.data
}

export const uploadPartnerStoreProfilePhoto = async (storeId, image, registrationToken) => {
  const formData = new FormData()
  formData.append("image", image)
  formData.append("registrationToken", registrationToken)

  const response = await api.post(`stores/${storeId}/image/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })

  return response.data
}

export const createStoreAdmin = async (values, registrationToken) => {
  const response = await api.post(endpoints.storeUsers, {
    registrationToken,
    user: {
      email: values.email.trim().toLowerCase(),
      fullName: values.fullName.trim(),
      plainPassword: values.password,
    },
  })

  return response.data
}

export const resendPartnerVerificationEmail = async email => {
  const response = await api.post(endpoints.resendVerification, {
    email: email.trim().toLowerCase(),
  })

  return response.data
}

export const resendPartnerVerificationEmailByStore = async storeId => {
  const response = await api.post(endpoints.resendVerification, {
    storeId,
  })

  return response.data
}

export const activatePartnerUser = async (email, verificationCode) => {
  const response = await api.post(endpoints.activateUser, {
    email: email.trim().toLowerCase(),
    verificationCode,
  })

  return response.data
}

export const activatePartnerUserByStore = async (storeId, verificationCode) => {
  const response = await api.post(endpoints.activateUser, {
    storeId,
    verificationCode,
  })

  return response.data
}

export const authenticatePartner = async (email, password) => {
  const response = await api.post(endpoints.auth, {
    email: email.trim().toLowerCase(),
    password,
    auth_type: "bo",
  })

  return response.data
}

export const getPartnerApiErrorMessage = (error, context) => {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  const description = data["hydra:description"] || data.detail || data.message || ""
  const violations = Array.isArray(data.violations)
    ? data.violations.map(violation => violation.message).join(" ")
    : ""
  const message = `${description} ${violations}`.toLowerCase()

  if (context === "store" && (status === 409 || message.includes("already") || message.includes("existe"))) {
    return "Ce commerce semble déjà inscrit. Connectez-vous ou contactez le support."
  }

  if (context === "user" && (status === 409 || message.includes("email") && (message.includes("already") || message.includes("existe")))) {
    return "Un compte existe déjà avec cette adresse email."
  }

  if (context === "image") {
    return "Le commerce a été créé, mais la photo de profil n’a pas pu être envoyée. Vous pourrez l’ajouter plus tard depuis les paramètres."
  }

  if (context === "activation") {
    return description || violations || "Le code de vérification est invalide ou expiré."
  }

  if (context === "login") {
    return description || violations || "Votre compte est activé, mais la connexion a échoué. Connectez-vous depuis la page de connexion."
  }

  if (!error?.response) {
    return "Impossible de contacter le service Wevalo. Vérifiez votre connexion puis réessayez."
  }

  return description || violations || "Une erreur est survenue. Veuillez réessayer."
}
